import { LayoutGrid, Plus } from "lucide-react"
import StyledSelectDropdown from "../StyledSelectDropdown"
import { FormField, FORM_LABEL_CLASS, MultiSelect } from "./CreateJobFields"

type Area = {
    fieldOfExpertiseId: number | null
    experienceLevel: number | null
    keySkillsIds: number[]
    niceSkillsIds: number[]
}

type AreasOfExpertiseSectionProps = {
    isEditMode: boolean
    areasOfExpertise: Area[]
    fieldsOfExpertise: any[]
    fieldsOfExpertiseStatus: string
    selectedJobLevelId: number | null
    keySkillsByExpertise: Record<string, Array<{ id: number; name: string }>>
    keySkillsLoadingByExpertise: Record<string, boolean>
    globalKeySkillIds: Set<number>
    globalNiceSkillIds: Set<number>
    getExperienceLevelOptions: () => Array<{ id: string; name: string }>
    addAreaOfExpertise: () => void
    removeAreaOfExpertise: (index: number) => void
    handleAreaOfExpertiseChange: (index: number, field: "fieldOfExpertiseId" | "experienceLevel", value: string) => void
    toggleKeySkill: (index: number, skillId: number) => void
    toggleNiceSkill: (index: number, skillId: number) => void
}

export const AreasOfExpertiseSection = ({
    isEditMode,
    areasOfExpertise,
    fieldsOfExpertise,
    fieldsOfExpertiseStatus,
    selectedJobLevelId,
    keySkillsByExpertise,
    keySkillsLoadingByExpertise,
    globalKeySkillIds,
    globalNiceSkillIds,
    getExperienceLevelOptions,
    addAreaOfExpertise,
    removeAreaOfExpertise,
    handleAreaOfExpertiseChange,
    toggleKeySkill,
    toggleNiceSkill,
}: AreasOfExpertiseSectionProps) => (
    <section className="mb-10">
        <div className="bg-[#E6EDDD] p-4 flex items-center justify-between rounded-md mb-6">
            <div className="flex items-center gap-3">
                <LayoutGrid className="w-5 h-5 text-gray-700" />
                <h2 className="font-bold text-gray-800 tracking-tight">Areas of expertise</h2>
            </div>
            {!isEditMode && (
                <Plus className="w-5 h-5 text-gray-700 cursor-pointer" onClick={addAreaOfExpertise} />
            )}
        </div>

        {areasOfExpertise.map((area, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mb-6">
                <FormField label="Field of expertise" required>
                    <StyledSelectDropdown
                        options={fieldsOfExpertise}
                        value={area.fieldOfExpertiseId || ""}
                        onChange={(value) => handleAreaOfExpertiseChange(index, "fieldOfExpertiseId", value)}
                        placeholder="Select field of expertise"
                        disabled={fieldsOfExpertiseStatus === "loading" || isEditMode}
                    />
                </FormField>

                <FormField label="Years of experience" required>
                    <StyledSelectDropdown
                        options={getExperienceLevelOptions()}
                        value={area.experienceLevel !== null && area.experienceLevel !== undefined ? String(area.experienceLevel) : ""}
                        onChange={(value) => handleAreaOfExpertiseChange(index, "experienceLevel", value)}
                        placeholder="Select Years of experience"
                        disabled={!selectedJobLevelId || isEditMode}
                    />
                </FormField>

                <div className="flex flex-col gap-2">
                    <div className="flex justify-between">
                        <label className={FORM_LABEL_CLASS}>Key skills (must have)</label>
                    </div>
                    <MultiSelect
                        options={area.fieldOfExpertiseId
                            ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || [])
                                .filter((skill) => {
                                    const blockedByOtherKey = globalKeySkillIds.has(skill.id) && !area.keySkillsIds.includes(skill.id)
                                    const blockedByNice = globalNiceSkillIds.has(skill.id) && !area.keySkillsIds.includes(skill.id)
                                    return !blockedByOtherKey && !blockedByNice
                                })
                                .map((skill) => skill.name)
                            : []}
                        selected={area.keySkillsIds.map((id) => {
                            const skills = area.fieldOfExpertiseId ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || []) : []
                            const skill = skills.find((item) => item.id === id)
                            return skill ? skill.name : ""
                        }).filter(Boolean)}
                        onToggle={(value) => {
                            if (!isEditMode) {
                                const skills = area.fieldOfExpertiseId ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || []) : []
                                const skill = skills.find((item) => item.name === value)
                                if (skill) {
                                    toggleKeySkill(index, skill.id)
                                }
                            }
                        }}
                        placeholder={
                            !area.fieldOfExpertiseId
                                ? "Select field of expertise first"
                                : keySkillsLoadingByExpertise[String(area.fieldOfExpertiseId)]
                                    ? "Loading..."
                                    : "Select key skills"
                        }
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex justify-between">
                        <label className={FORM_LABEL_CLASS}>Skills (nice to have)</label>
                    </div>
                    <MultiSelect
                        options={area.fieldOfExpertiseId
                            ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || [])
                                .filter((skill) => !globalKeySkillIds.has(skill.id))
                                .map((skill) => skill.name)
                            : []}
                        selected={area.niceSkillsIds.map((id) => {
                            const skills = area.fieldOfExpertiseId ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || []) : []
                            const skill = skills.find((item) => item.id === id)
                            return skill ? skill.name : ""
                        }).filter(Boolean)}
                        onToggle={(value) => {
                            if (!isEditMode) {
                                const skills = area.fieldOfExpertiseId ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || []) : []
                                const skill = skills.find((item) => item.name === value)
                                if (skill) {
                                    toggleNiceSkill(index, skill.id)
                                }
                            }
                        }}
                        placeholder={
                            !area.fieldOfExpertiseId
                                ? "Select field of expertise first"
                                : keySkillsLoadingByExpertise[String(area.fieldOfExpertiseId)]
                                    ? "Loading..."
                                    : "Select nice-to-have skills"
                        }
                    />
                </div>

                {areasOfExpertise.length > 1 && !isEditMode && (
                    <div className="flex justify-end col-span-2">
                        <button
                            className="text-red-400 text-xs mt-1 font-medium"
                            onClick={() => removeAreaOfExpertise(index)}
                        >
                            Remove
                        </button>
                    </div>
                )}
            </div>
        ))}
        {!isEditMode && (
            <p className="mt-2 text-paragraph font-bold text-gray-500">To add more Requirements click the + icon.</p>
        )}
    </section>
)
